<template>
  <ion-app>
    <ion-page>
      <ion-header>
        <ion-toolbar>
          <ion-title>{{ whitelabelI18n.appTitle }}</ion-title>
          <ion-buttons slot="end">
            <language-switcher 
              v-if="whitelabelUserIntegration.isLanguageSwitchingAvailable.value"
              :languages="[
                { code: 'en', name: 'English' },
                { code: 'et', name: 'Eesti' }
              ]"
            ></language-switcher>
            <user-switcher 
              v-if="whitelabelUserIntegration.isUserSwitchingAvailable.value"
            ></user-switcher>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>

      <ion-tabs>
        <ion-router-outlet
          :animated="true"
          animation="ios-transition"
        ></ion-router-outlet>

        <ion-tab-bar slot="bottom" style="--ion-tab-bar-color: var(--ion-color-medium); --ion-tab-bar-color-selected: var(--ion-color-primary);">
          <ion-tab-button tab="home" href="/home" class="py-2">
            <ion-icon :icon="homeOutline" class="!text-base sm:!text-lg !min-w-4 sm:!min-w-[18px] !min-h-4 sm:!min-h-[18px] mb-[3px]" />
            <ion-label class="text-[10px] sm:text-xs whitespace-nowrap overflow-hidden text-ellipsis min-h-[14px] mt-1">{{ $t('app.tabs.home') }}</ion-label>
          </ion-tab-button>

          <ion-tab-button 
            v-if="whitelabelUserIntegration.isProfilePageAvailable.value"
            tab="profile" 
            href="/profile" 
            class="py-2"
          >
            <ion-icon :icon="personOutline" class="!text-base sm:!text-lg !min-w-4 sm:!min-w-[18px] !min-h-4 sm:!min-h-[18px] mb-[3px]" />
            <ion-label class="text-[10px] sm:text-xs whitespace-nowrap overflow-hidden text-ellipsis min-h-[14px] mt-1">{{ $t('app.tabs.profile') }}</ion-label>
          </ion-tab-button>

          <!-- Leaderboard tab shown if both whitelabel feature is enabled AND user is advanced segment -->
          <ion-tab-button 
            v-if="whitelabelUserIntegration.isLeaderboardAvailable.value" 
            tab="leaderboard" 
            href="/leaderboard"
            data-cy="leaderboard-tab"
            class="py-2"
          >
            <ion-icon :icon="podiumOutline" class="!text-base sm:!text-lg !min-w-4 sm:!min-w-[18px] !min-h-4 sm:!min-h-[18px] mb-[3px]" />
            <ion-label class="text-[10px] sm:text-xs whitespace-nowrap overflow-hidden text-ellipsis min-h-[14px] mt-1">{{ $t('app.tabs.leaderboard') }}</ion-label>
          </ion-tab-button>

          <ion-tab-button tab="rewards" href="/rewards" class="py-2">
            <ion-icon :icon="giftOutline" class="!text-base sm:!text-lg !min-w-4 sm:!min-w-[18px] !min-h-4 sm:!min-h-[18px] mb-[3px]" />
            <ion-label class="text-[10px] sm:text-xs whitespace-nowrap overflow-hidden text-ellipsis min-h-[14px] mt-1">{{ $t('app.tabs.rewards') }}</ion-label>
          </ion-tab-button>
        </ion-tab-bar>
      </ion-tabs>
    </ion-page>
  </ion-app>
</template>

<script setup lang="ts">
import {
  IonApp,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonButtons
} from '@ionic/vue';
import {
  homeOutline,
  personOutline,
  podiumOutline,
  giftOutline
} from 'ionicons/icons';
import { onMounted } from 'vue'
import { whenever } from '@vueuse/core'
import { useUserStore } from './stores/userStore'
import { useLanguageEvents } from './composables/useLanguageEvents'
import { useWhitelabelBranding } from './composables/useWhitelabelConfig'
import { useWhitelabelUserIntegration } from './composables/useWhitelabelUserIntegration'
import { useWhitelabelI18n } from './composables/useWhitelabelI18n'
import UserSwitcher from './components/UserSwitcher.vue';
import LanguageSwitcher from './components/LanguageSwitcher.vue';

const userStore = useUserStore()
const { onLanguageChanged } = useLanguageEvents()

// Whitelabel configuration integration - Subtask 7.1 & 7.2
const whitelabelBranding = useWhitelabelBranding()
const whitelabelUserIntegration = useWhitelabelUserIntegration()
const whitelabelI18n = useWhitelabelI18n()

onMounted(async () => {
  await userStore.loadUser()
  
  // Log whitelabel features in development
  if (import.meta.env.DEV) {
    console.log('🎨 Whitelabel user integration status:', whitelabelUserIntegration.getUserFeatureStatus())
    console.log('🏷️ Whitelabel branding applied:', {
      appName: whitelabelBranding.appName,
      appTitle: whitelabelI18n.appTitle.value,
      primaryColor: whitelabelBranding.primaryColor,
      hasCustomBranding: whitelabelBranding.hasCustomBranding,
      usingI18nOverride: whitelabelI18n.isUsingWhitelabelOverride('app.title')
    })
  }
})

// Use VueUse whenever to react to user changes
whenever(() => userStore.user, (user) => {
  if (user) {
    console.log('User changed, segment:', userStore.userSegment)
  }
})

// Listen for language changes using VueUse composable
onLanguageChanged(({ lang }) => {
  // Update DOM in presentation layer
  document.querySelector('html')?.setAttribute('lang', lang)
})
</script> 